import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Defaults as BaseDefaults } from '@opentripplanner/itinerary-body';
import { Defaults as MLDefaults } from '@opentripplanner/itinerary-body-ml';
import coreUtils from '@opentripplanner/core-utils';
import { useIntl } from 'react-intl';
import { toModeColor, modeFromLeg } from '#/trip-planner/utils/mode';
import {
  MICROMOBILITY_MODE,
  MICROMOBILITY_STARTING_TYPE,
  BICYCLE_MODE,
  CAR_MODE,
  FEATURE_FLAGS,
} from '#/shared/constants';
import RouteBadge from '#/shared/components/RouteBadge';
import RentalBadge from '#/shared/components/RentalBadge';
import { parseOtpId } from '#/trip-planner/utils/itinerary';
import TripSummaryButton from '#/trip-planner/components/TripSummaryButton';
import useIntlDatetime from '#/shared/hooks/useIntlDatetime';
import * as Styled from './styled';

const { MAPLIBRE_MODE } = FEATURE_FLAGS;
const DefaultsModule = MAPLIBRE_MODE ? MLDefaults : BaseDefaults;

const { transitModes } = coreUtils.itinerary;
const { Duration } = DefaultsModule;

const getBadge = leg => {
  if (transitModes.includes(leg.mode)) {
    return <RouteBadge routeId={parseOtpId(leg.routeId)} />;
  }

  if (
    ((leg.rentedVehicle || leg.rentedBike || leg.rentedCar) &&
      leg.from.networks) ||
    (leg.hailedCar && leg.tncData.company)
  ) {
    return (
      <RentalBadge
        company={leg.hailedCar ? leg.tncData.company : leg.from.networks[0]}
        color={toModeColor(leg.mode)}
      />
    );
  }
  return null;
};

const createRouteIconElement = (leg, id, withSpacerIcon) => (
  <Fragment key={id}>
    <Styled.TripModeSummaryRouteIconColumn>
      <Styled.VehicleIcon mode={modeFromLeg(leg)} />
      <Styled.RouteBadgeWrapper>{getBadge(leg)}</Styled.RouteBadgeWrapper>
    </Styled.TripModeSummaryRouteIconColumn>
    {withSpacerIcon && (
      <Styled.TripModeSummaryIconSpacerColumn>
        <Styled.ChevronIcon />
      </Styled.TripModeSummaryIconSpacerColumn>
    )}
  </Fragment>
);

const convertToCurrency = (cents, currencyCode) => {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: currencyCode,
  });
};

const generateTransferCount = (
  transferCount,
  singleTransferStr,
  multipleTransfersStr
) => {
  if (transferCount > 0) {
    return `, ${transferCount} ${
      transferCount === 1 ? singleTransferStr : multipleTransfersStr
    }`;
  }
  return '';
};

// e-scooters tend to regularly have walking legs that split up a single trip,
// similar to an interline trip, but the only way to differentiate the 2 legs
// has been using the check below
const isEscootersFirstLeg = leg =>
  leg.mode === MICROMOBILITY_MODE &&
  leg.from.vertexType === MICROMOBILITY_STARTING_TYPE;

function TripModeSummary({
  itinerary,
  toAddress,
  fromAddress,
  navigateTripPlanner,
}) {
  const intl = useIntl();
  const { milliSecondsToTriMetDateString, milliSecondsToTriMetTimeString } =
    useIntlDatetime();

  const itineraryIconLegs = itinerary.legs.filter(
    // don't need to iconize walk access legs or interline legs
    leg =>
      ([...transitModes, BICYCLE_MODE, CAR_MODE].includes(leg.mode) ||
        isEscootersFirstLeg(leg)) &&
      leg.interlineWithPreviousLeg === false
  );

  const legs = itineraryIconLegs
    .slice(0, -1)
    .map((l, idx) => createRouteIconElement(l, idx, true));

  if (itineraryIconLegs && itineraryIconLegs.length > 0) {
    legs.push(
      createRouteIconElement(
        itineraryIconLegs.splice(-1).pop(),
        itineraryIconLegs.length,
        false
      )
    );
  }

  return (
    <Styled.TripModeSummary>
      <TripSummaryButton
        fromAddress={fromAddress}
        toAddress={toAddress}
        tripPlannerHandler={navigateTripPlanner}
      />
      <Styled.TripModeInfoRow>
        <Styled.TimeSpan>
          {milliSecondsToTriMetTimeString(itinerary.startTime)}–
          {milliSecondsToTriMetTimeString(itinerary.endTime)}
          {', '}
          {milliSecondsToTriMetDateString(itinerary.startTime, true)}
        </Styled.TimeSpan>
        <Styled.TripInfoSpan>
          <Duration seconds={itinerary.duration} />
          {generateTransferCount(
            itinerary.transfers,
            intl.formatMessage({ id: 'planner.singletransfer' }),
            intl.formatMessage({ id: 'planner.multipletransfers' })
          )}
          {itinerary.fare &&
            `, ${convertToCurrency(
              itinerary.fare.cents,
              itinerary.fare.currencyCode
            )}`}
        </Styled.TripInfoSpan>
      </Styled.TripModeInfoRow>
      <Styled.TripModeLegIconRow>
        {itinerary.legs && legs}
      </Styled.TripModeLegIconRow>
    </Styled.TripModeSummary>
  );
}

TripModeSummary.propTypes = {
  /** Function to navigate to the trip planner */
  navigateTripPlanner: PropTypes.func.isRequired,
  /** Address the user is traveling to */
  toAddress: PropTypes.string.isRequired,
  /** Address the user is traveling from */
  fromAddress: PropTypes.string.isRequired,
  /** Itinerary that the user has selected to view, contains multiple legs */
  itinerary: PropTypes.shape({
    duration: PropTypes.number.isRequired,
    startTime: PropTypes.number.isRequired,
    endTime: PropTypes.number.isRequired,
    transfers: PropTypes.number.isRequired,
    legs: PropTypes.arrayOf(
      PropTypes.shape({
        transitLeg: PropTypes.bool.isRequired,
      }).isRequired
    ).isRequired,
    fare: PropTypes.shape({
      cents: PropTypes.number.isRequired,
      currencyCode: PropTypes.string.isRequired,
    }),
  }).isRequired,
};
export default TripModeSummary;
